import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import api from '../../api/client';

interface AgencyType {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export default function AgencyTypesPage() {
  const [types, setTypes] = useState<AgencyType[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AgencyType | null>(null);
  const [form, setForm] = useState({ code: '', name: '', sort_order: 0 });

  const load = () => api.get('/agency-types').then((r) => setTypes(r.data));
  useEffect(() => { load(); }, []);

  const handleOpen = (at?: AgencyType) => {
    if (at) {
      setEditing(at);
      setForm({ code: at.code, name: at.name, sort_order: at.sort_order });
    } else {
      setEditing(null);
      setForm({ code: '', name: '', sort_order: 0 });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/agency-types/${editing.id}`, form);
    } else {
      await api.post('/agency-types', form);
    }
    setOpen(false);
    load();
  };

  const toggleActive = async (at: AgencyType) => {
    await api.patch(`/agency-types/${at.id}`, { is_active: !at.is_active });
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Agency Types</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Add Agency Type</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((at) => (
              <TableRow key={at.id}>
                <TableCell>{at.code}</TableCell>
                <TableCell>{at.name}</TableCell>
                <TableCell>{at.sort_order}</TableCell>
                <TableCell>
                  <Chip label={at.is_active ? 'Active' : 'Inactive'} color={at.is_active ? 'success' : 'default'} size="small" onClick={() => toggleActive(at)} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(at)}><Edit fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {types.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">No agency types defined</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Agency Type' : 'Add Agency Type'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} margin="dense" />
          <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} margin="dense" />
          <TextField fullWidth label="Sort Order" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} margin="dense" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
